import type { DataTableColumn, DataTableRow } from '@ui/chart/DataTable';
import { BiBookingChange, BiBookingCut, BiBookingRow, BiBookingStatus, RevenueScope, VatBasis } from '@app/model/bi/Bi';
import { revenueOf } from '../Bi.utilities';

// The bookings behind a number of a BI table (ROO2-284, grill D9 and Q1–Q10 of 2026-10-26). A click on RN or REV of today,
// of a situation D-n, of STLY, of LY actual or of a PU opens the list; ADR, SHARE, Δ%, the like-for-like columns, the
// build-up and the total row do not (Greg 2026-10-25, Q2). The list uses the nights and the cuts of the cell, so its total
// equals the cell.

export type BookingMeasure = 'rn' | 'rev';
export type BookingCell = { cut: BiBookingCut, measure: BookingMeasure };

const DAYS = ['1', '3', '7', '14', '30'];
// The cut and the measure of a column id of a BI table (the ids of Channels.utilities columnsOf); null: not clickable.
export const bookingCellOf = (columnId: string): BookingCell | null => {
	const measure: BookingMeasure | null = columnId.endsWith('Rn') || columnId === 'rn' ? 'rn' : (columnId.endsWith('Rev') || columnId === 'rev' ? 'rev' : null);
	if (measure === null) return null;
	const base = columnId === 'rn' || columnId === 'rev' ? '' : columnId.slice(0, -(measure === 'rn' ? 2 : 3));
	if (base === '') return { cut: 'D0', measure };
	if (base === 'stly') return { cut: 'STLY', measure };
	if (base === 'ly') return { cut: 'LY_ACTUAL', measure };
	const d = /^(d|pu)(\d+)$/.exec(base);
	if (d === null || !DAYS.includes(d[2])) return null;
	return { cut: `${d[1] === 'd' ? 'D' : 'PU'}${d[2]}` as BiBookingCut, measure };
};

export const isPickup = (cut: BiBookingCut): boolean => cut.startsWith('PU');

// The name of a cut as the column groups of the table say it.
export const cutLabel = (cut: BiBookingCut): string => {
	if (cut === 'D0') return 'Today';
	if (cut === 'STLY') return 'STLY';
	if (cut === 'LY_ACTUAL') return 'LY actual';
	return cut.startsWith('PU') ? `PU ${cut.slice(2)}` : `Situation D-${cut.slice(1)}`;
};

export const CHANGE_LABEL: Record<BiBookingChange, string> = {
	NEW: 'New', LONGER: 'Longer', MOVED_IN: 'Moved in', SHORTER: 'Shorter', CANCELLED: 'Cancelled', MOVED_OUT: 'Moved out', PRICE: 'Price',
};
export const STATUS_LABEL: Record<BiBookingStatus, string> = { SOLD: 'Sold', CANCELLED_LATER: 'Cancelled later' };

// The columns of the list (Q1, Q4, Q6). The property shows only for a scope of more than one property. Every column sorts.
export const bookingColumns = (cut: BiBookingCut, manyProperties: boolean): DataTableColumn[] => {
	const pu = isPickup(cut);
	return [
		// ROO2-314 D3: the reservation number (a 5stelle code without its booking time) and the OTA confirmation number.
		{ id: 'code', label: 'Reservation' },
		{ id: 'ota', label: 'OTA number' },
		...(manyProperties ? [{ id: 'property', label: 'Property' }] : []),
		{ id: 'channel', label: 'Channel name' },
		{ id: 'booked', label: 'Booked', format: 'date' },
		{ id: 'arrival', label: 'Arrival', format: 'date' },
		{ id: 'departure', label: 'Departure', format: 'date' },
		{ id: 'nights', label: 'Nights', format: 'number' },
		{ id: 'rooms', label: 'Rooms', format: 'number' },
		pu ? { id: 'rn', label: 'Δ RN', format: 'number', signed: true, tone: 'change' } : { id: 'rn', label: 'RN', format: 'number' },
		pu ? { id: 'rev', label: 'Δ REV', format: 'money', signed: true, tone: 'change' } : { id: 'rev', label: 'REV', format: 'money' },
		{ id: 'state', label: pu ? 'Change' : 'Status' },
	];
};

export const bookingRowId = (b: BiBookingRow): string => `${b.companyHotelId}|${b.reservationCode}`;

// The rows of the list, in the revenue of the switches.
export const bookingTableRows = (bookings: BiBookingRow[], scope: RevenueScope, vat: VatBasis, propertyName: (id: number) => string): DataTableRow[] =>
	bookings.map(b => ({
		id: bookingRowId(b),
		cells: {
			code: b.reservationNumber, ota: b.otaNumber ?? '–', property: propertyName(b.companyHotelId), channel: b.channelName.trim() === '' ? 'No channel' : b.channelName,
			booked: b.bookDate, arrival: b.arrival, departure: b.departure, nights: b.nights, rooms: b.rooms,
			rn: b.roomNights, rev: revenueOf(b, scope, vat),
			state: b.change !== null ? CHANGE_LABEL[b.change] : (b.status !== null ? STATUS_LABEL[b.status] : ''),
		},
	}));

// A sum of revenues: null when one value is null (never a partial sum, ROO2-236 R2).
const sumRevenue = (values: (number | null)[]): number | null =>
	values.some(v => v === null) ? null : Math.round(values.reduce<number>((t, v) => t + (v as number), 0) * 100) / 100;

export type BookingTotals = {
	bookings: number, roomNights: number, revenue: number | null,
	// A PU list: the + rows (room nights up, or revenue up with the same room nights) and the − rows.
	up: { bookings: number, roomNights: number }, down: { bookings: number, roomNights: number },
};

// The total of the list: it equals the cell (AC1–AC3). In a PU list it is the sum of the changes.
export const bookingTotals = (bookings: BiBookingRow[], scope: RevenueScope, vat: VatBasis): BookingTotals => {
	const sign = (b: BiBookingRow) => b.roomNights !== 0 ? Math.sign(b.roomNights) : Math.sign(revenueOf(b, scope, vat) ?? 0);
	const up = bookings.filter(b => sign(b) > 0), down = bookings.filter(b => sign(b) < 0);
	const rn = (list: BiBookingRow[]) => list.reduce((t, b) => t + b.roomNights, 0);
	return {
		bookings: bookings.length, roomNights: rn(bookings), revenue: sumRevenue(bookings.map(b => revenueOf(b, scope, vat))),
		up: { bookings: up.length, roomNights: rn(up) }, down: { bookings: down.length, roomNights: rn(down) },
	};
};

// The footer of the list: the total of the room nights and the revenue.
export const bookingFooter = (totals: BookingTotals): DataTableRow => ({
	id: 'total', cells: { code: `Total (${totals.bookings})`, rn: totals.roomNights, rev: totals.revenue },
});
